import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Roster } from '../../models/roster';
import { CommonModule } from '@angular/common';
import { Campaign } from '../../models/campaign';
import { BaseChartDirective } from 'ng2-charts';
import { RouterModule } from '@angular/router';
import { RevenueChartComponent } from '../revenue-chart/revenue-chart.component';
import { FormsModule } from '@angular/forms';
import { CampaignService } from '../../services/campaign.service';
import { RosterService } from '../../services/roster.service';
import { UserService } from '../../services/user.service';
import { User, UserRole } from '../../models/user';
import { NotificationsComponent } from "../notifications/notifications.component";
import { AddNewCampaignDialogComponent } from '../add-new-campaign-dialog/add-new-campaign-dialog.component';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { Company } from '../../models/company';


export type RevenueData = {
  month: string,
  revenue: number
}

export enum RevenueType {
  Realized,
  Projected
}

export type Creator = {
  id: string,
  name: string
}

@Component({
  selector: 'app-agent-dashboard',
  imports: [CommonModule, RouterModule, RevenueChartComponent, FormsModule, NotificationsComponent, AddNewCampaignDialogComponent],
  templateUrl: './agent-dashboard.component.html',
  styleUrl: './agent-dashboard.component.css'
})
export class AgentDashboardComponent implements OnInit, OnDestroy {

  Math = Math;
  private destroy$ = new Subject<void>();
  private creatorFilterInput$ = new Subject<string>();
  campaignNameFilter: string = '';
  brandFilter: string = '';
  agentFilter: string = 'All';
  creatorFilter: string = '';
  coordinatorFilter: string = 'All';
  sourceFilter: string = 'All';
  idFilter: string = '';
  errorMessage: string = '';

  nonDeletedCampaigns: Campaign[] = [];
  campaigns: Campaign[] = [];

  // Bumped each time the due-tasks call completes so the Tasks table (app-notifications) re-reads
  // the campaigns' tasks.
  dueTasksVersion: number = 0;
  // Campaigns matching every filter except the chart's month/day drill-down - this feeds the chart
  chartCampaigns: Campaign[] = [];
  campaignsFiltered: Campaign[] = this.campaigns;

  // Chart drill-down selection (YYYY-MM / YYYY-MM-DD) applied on top of chartCampaigns
  selectedMonthKey: string | null = null;
  selectedDayKey: string | null = null;

  // Which info box drives the chart - 'bookings' (Gross Bookings) or 'count' (Campaign Count)
  chartMetric: 'bookings' | 'count' = 'bookings';

  campaignCount: number = 0;
  showAddNewCampaignDialog: boolean = false;
  topSectionVisible: boolean = true;

  // Phone layout: the info boxes / chart / task table are dropped and the campaign
  // table is rendered as tiles instead of grid rows.
  static readonly SmallScreenQuery = '(max-width: 768px)';
  isSmallScreen: boolean = false;
  mobileFiltersOpen: boolean = false;
  private smallScreenMediaQuery?: MediaQueryList;
  private onSmallScreenChange = (e: MediaQueryListEvent | MediaQueryList) => { this.isSmallScreen = e.matches; };

  agentUsers: User[] = [];
  allUsers: User[] = [];

  companies: Company[] = [];

  @ViewChild(RevenueChartComponent)
  revenueChart?: RevenueChartComponent;

  constructor(private campaignService: CampaignService, private rosterService: RosterService, private userService: UserService, private companyService: CompanyService) {}

  resolveUserId(id: string): string { return this.userService.resolveUserId(id); }

  toggleTopSection(): void {
    this.topSectionVisible = !this.topSectionVisible;
    try { localStorage.setItem('agentDashboard.topSectionVisible', String(this.topSectionVisible)); } catch (e) { /* ignore */ }
  }

  getAccountName(accountId: string | undefined): string {
    if (!accountId) return '';
    return this.companies.find(c => c.id === accountId)?.name ?? '';
  }

  getBrandName(brandId: string | undefined): string {
    if (!brandId) return '';
    return this.companies.find(c => c.id === brandId)?.name ?? '';
  }

  truncate(text: string | undefined | null, max = 20): string {
    if (!text) return '';
    return text.length > max ? text.substring(0, max) + '…' : text;
  }

  rosters: Roster[] = [];


  agents: Set<string> = new Set<string>();
  coordinators: Set<string> = new Set<string>();

  showDeleted: boolean = false;
  showCompleted: boolean = false;

  ngOnDestroy(): void {
    this.smallScreenMediaQuery?.removeEventListener('change', this.onSmallScreenChange);
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMobileFilters(): void {
    this.mobileFiltersOpen = !this.mobileFiltersOpen;
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.smallScreenMediaQuery = window.matchMedia(AgentDashboardComponent.SmallScreenQuery);
      this.isSmallScreen = this.smallScreenMediaQuery.matches;
      this.smallScreenMediaQuery.addEventListener('change', this.onSmallScreenChange);
    }

    this.creatorFilterInput$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(value => {
      this.creatorFilter = value;
      try { localStorage.setItem('agentDashboard.creatorFilter', value); } catch (e) { /* ignore */ }
      this.applyAllFilters();
    });

    this.agents.add("All");
    this.coordinators.add("All");

    this.userService.getUsers().subscribe(users => {
      this.allUsers = users;
      this.agentUsers = users.filter(u => u.role === UserRole.Agent || u.role === UserRole.Scouting || u.role === UserRole.Casting);
    });

    // Read persisted filter settings from localStorage
    try {
      const sc = localStorage.getItem('agentDashboard.showCompleted');
      const sd = localStorage.getItem('agentDashboard.showDeleted');
      const cf = localStorage.getItem('agentDashboard.creatorFilter');
      const af = localStorage.getItem('agentDashboard.agentFilter');
      const cof = localStorage.getItem('agentDashboard.coordinatorFilter');
      const cnf = localStorage.getItem('agentDashboard.campaignNameFilter');
      const bf = localStorage.getItem('agentDashboard.brandFilter');
      const sf = localStorage.getItem('agentDashboard.sourceFilter');
      const idf = localStorage.getItem('agentDashboard.idFilter');

      if (sc !== null) this.showCompleted = sc === 'true';
      if (sd !== null) this.showDeleted = sd === 'true';
      if (cf !== null) this.creatorFilter = cf;
      if (af !== null) this.agentFilter = af;
      if (cof !== null) this.coordinatorFilter = cof;
      if (cnf !== null) this.campaignNameFilter = cnf;
      if (bf !== null) this.brandFilter = bf;
      if (sf !== null) this.sourceFilter = sf;
      if (idf !== null) this.idFilter = idf;
      const tsv = localStorage.getItem('agentDashboard.topSectionVisible');
      if (tsv !== null) this.topSectionVisible = tsv === 'true';
      const cm = localStorage.getItem('agentDashboard.chartMetric');
      if (cm === 'bookings' || cm === 'count') this.chartMetric = cm;
    } catch (e) { /* ignore storage errors */ }

    // Use forkJoin to wait for campaigns, rosters, and accounts
    forkJoin({
      campaigns: this.campaignService.getCampaigns(),
      rosters: this.rosterService.getRosters(),
      companies: this.companyService.getCompanies()
    }).subscribe({
      next: (result) => {
        this.campaigns = result.campaigns;
        this.rosters = result.rosters;
        this.companies = result.companies;

        // Populate creator names from rosters lookup
        this.populateCreatorNames();

        this.campaignCount = this.campaigns.filter(c => c.deleted === false).length;
        this.nonDeletedCampaigns = this.campaigns.filter(c => c.deleted === false);

        this.campaigns.forEach(campaign => {
          if (campaign.agentId && typeof campaign.agentId === 'string') {
            this.agents.add(campaign.agentId);
          }
          if (campaign.coordinatorId && typeof campaign.coordinatorId === 'string') {
            this.coordinators.add(campaign.coordinatorId);
          }
        });

        // Sort coordinators alphabetically and add 'All' at the top
        const sortedCoordinators = Array.from(this.coordinators)
          .filter((name): name is string => !!name && typeof name === 'string')
          .sort((a, b) => a.localeCompare(b));
        this.coordinators = new Set(['All', ...sortedCoordinators]);

        // Validate persisted filters against actual data
        if (this.agentFilter !== 'All' && !this.agents.has(this.agentFilter)) {
          console.warn(`Persisted agent filter '${this.agentFilter}' not found in current data, resetting to 'All'`);
          this.agentFilter = 'All';
        }
        if (this.coordinatorFilter !== 'All' && !this.coordinators.has(this.coordinatorFilter)) {
          console.warn(`Persisted coordinator filter '${this.coordinatorFilter}' not found in current data, resetting to 'All'`);
          this.coordinatorFilter = 'All';
        }

        // apply persisted filters immediately
        this.updateFilteredCampaigns();

        // The Tasks table (top right) and the status colours only need incomplete tasks that are
        // past due or due today: one separate call, made once the campaigns are in the cache. No
        // other tasks are loaded for the dashboard.
        this.campaignService.loadDueTasks().subscribe({
          next: () => { this.dueTasksVersion++; },
          error: (err) => { console.error('Failed to load due tasks', err); }
        });

      },
      error: (err) => {
        if (err.status === 401) {
          this.errorMessage = 'Authentication failed. Please logout, login and try again.'
        } else {
          console.log(err);
        }
      }
    })
  }

  scrollRight() {
    const el = document.getElementById('roster');
    el?.scrollBy({ left: 150, behavior: 'smooth' });
  }

  scrollLeft() {
    const el = document.getElementById('roster');
    el?.scrollBy({ left: -150, behavior: 'smooth' });
  }

  populateCreatorNames(): void {
    this.campaigns.forEach(campaign => {
      if (campaign.creatorId && typeof campaign.creatorId === 'string' && campaign.creatorId.trim() !== '') {
        const matchingRoster = this.rosters.find(roster => roster.id === campaign.creatorId);
        if (matchingRoster) {
          campaign.creatorName = matchingRoster.name;
          campaign.tiktokHandle = matchingRoster.tiktokHandle;
        } else {
          console.warn(`No roster found with id: ${campaign.creatorId}`);
        }
      }
    });
  }

  campaignNameFilterChange(event: Event) {
    const campaignNameFilterInput = event.target as HTMLInputElement;
    this.campaignNameFilter = campaignNameFilterInput.value;

    // Save campaign name filter to localStorage
    try {
      localStorage.setItem('agentDashboard.campaignNameFilter', this.campaignNameFilter);
    } catch (e) { /* ignore storage errors */ }

    this.applyAllFilters();
  }

  brandFilterChange(event: Event) {
    this.brandFilter = (event.target as HTMLInputElement).value;
    try { localStorage.setItem('agentDashboard.brandFilter', this.brandFilter); } catch (e) { /* ignore */ }
    this.applyAllFilters();
  }

  idFilterChange(event: Event) {
    this.idFilter = (event.target as HTMLInputElement).value;
    try { localStorage.setItem('agentDashboard.idFilter', this.idFilter); } catch (e) { /* ignore */ }
    this.applyAllFilters();
  }

  applyAllFilters() {
    // Precompute source filter data outside the loop
    let denaId: string | null = null;
    let amandaRosterIds: Set<string> | null = null;
    if (this.sourceFilter === 'Dena') {
      const dena = this.allUsers.find(u => u.firstName === 'Dena');
      if (dena) denaId = dena.id;
    } else if (this.sourceFilter === 'Amanda') {
      const amanda = this.allUsers.find(u => u.firstName === 'Amanda');
      if (amanda) {
        amandaRosterIds = new Set(this.rosters.filter(r => r.source === amanda.id).map(r => r.id));
      }
    }

    this.chartCampaigns = this.campaigns.filter(c => {
      // Apply deleted/completed filters
      const deletedOk = this.showDeleted || !c.deleted;
      const completedOk = this.showCompleted || !c.complete;

      // Apply campaign name filter
      const nameOk = !this.campaignNameFilter ||
                    c.name?.toLowerCase().includes(this.campaignNameFilter.toLowerCase());

      // Apply id filter (substring match on the full campaign id)
      const idOk = !this.idFilter ||
                    (c.id ?? '').toLowerCase().includes(this.idFilter.toLowerCase());

      // Apply brand/agency filter (text search on brand or account name)
      const brandOk = !this.brandFilter ||
                    this.getBrandName(c.brandId).toLowerCase().includes(this.brandFilter.toLowerCase()) ||
                    this.getAccountName(c.accountId).toLowerCase().includes(this.brandFilter.toLowerCase());

      // Apply agent filter
      const agentOk = this.agentFilter === 'All' || c.agentId === this.agentFilter;

      // Apply creator filter (text search on creator name or handle)
      const creatorOk = !this.creatorFilter ||
        (c.creatorName ?? '').toLowerCase().includes(this.creatorFilter.toLowerCase()) ||
        (c.tiktokHandle ?? '').toLowerCase().includes(this.creatorFilter.toLowerCase());

      // Apply coordinator filter
      const coordinatorOk = this.coordinatorFilter === 'All' || c.coordinatorId === this.coordinatorFilter;

      // Apply source filter
      let sourceOk = true;
      if (this.sourceFilter === 'Dena') {
        sourceOk = denaId !== null ? c.source === denaId : true;
      } else if (this.sourceFilter === 'Amanda') {
        sourceOk = amandaRosterIds !== null ? amandaRosterIds.has(c.creatorId) : true;
      } else if (this.sourceFilter === 'Inbound') {
        sourceOk = c.source === 'Inbound';
      } else if (this.sourceFilter === 'Outbound') {
        sourceOk = c.source === 'Outbound';
      }

      return deletedOk && completedOk && nameOk && idOk && brandOk && agentOk && creatorOk && coordinatorOk && sourceOk;
    }).sort((a, b) => {
      // Sort by created date (most recent first)
      const dateA = new Date(a.createdDate || 0);
      const dateB = new Date(b.createdDate || 0);
      return dateB.getTime() - dateA.getTime();
    });

    // A filter change rebuilds the chart, which drops its drill-down - clear it here too
    this.selectedMonthKey = null;
    this.selectedDayKey = null;
    this.applyDateSelection();
  }

  // Narrow the campaign list to the month/day picked on the bookings chart
  private applyDateSelection() {
    if (this.selectedDayKey) {
      this.campaignsFiltered = this.chartCampaigns.filter(c => (c.createdDate ?? '').substring(0, 10) === this.selectedDayKey);
    } else if (this.selectedMonthKey) {
      this.campaignsFiltered = this.chartCampaigns.filter(c => (c.createdDate ?? '').substring(0, 7) === this.selectedMonthKey);
    } else {
      this.campaignsFiltered = this.chartCampaigns;
    }
  }

  onChartMonthSelected(monthKey: string | null) {
    this.selectedMonthKey = monthKey;
    this.selectedDayKey = null;
    this.applyDateSelection();
  }

  onChartDaySelected(dayKey: string | null) {
    this.selectedDayKey = dayKey;
    this.applyDateSelection();
  }

  setChartMetric(metric: 'bookings' | 'count') {
    if (this.chartMetric === metric) return;

    this.chartMetric = metric;
    try { localStorage.setItem('agentDashboard.chartMetric', metric); } catch (e) { /* ignore */ }

    // The chart rebuilds on the new metric and drops its drill-down - clear the list filter to match
    this.selectedMonthKey = null;
    this.selectedDayKey = null;
    this.applyDateSelection();
  }

  clearDateSelection() {
    this.revenueChart?.resetToMonthly();
    this.selectedMonthKey = null;
    this.selectedDayKey = null;
    this.applyDateSelection();
  }

  getDateSelectionLabel(): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const key = this.selectedDayKey ?? this.selectedMonthKey;
    if (!key) return '';

    const year = key.substring(0, 4);
    const month = monthNames[Number(key.substring(5, 7)) - 1] ?? '';
    if (this.selectedDayKey) {
      return `${month} ${Number(this.selectedDayKey.substring(8, 10))}, ${year}`;
    }
    return `${month} ${year}`;
  }

  updateFilteredCampaigns() {
    // persist the user's filter choices so they survive route changes
    try {
      localStorage.setItem('agentDashboard.showCompleted', String(this.showCompleted));
      localStorage.setItem('agentDashboard.showDeleted', String(this.showDeleted));
    } catch (e) { /* ignore storage errors */ }

    this.applyAllFilters();
  }


  agentFilterChange(event: Event) {
    const agentSelect = event.target as HTMLSelectElement;
    this.agentFilter = agentSelect.value;

    // Save agent filter to localStorage
    try {
      localStorage.setItem('agentDashboard.agentFilter', this.agentFilter);
    } catch (e) { /* ignore storage errors */ }

    this.applyAllFilters();
  }

  creatorFilterChange(event: Event) {
    this.creatorFilterInput$.next((event.target as HTMLInputElement).value);
  }

  coordinatorFilterChange(event: Event) {
    const coordinatorSelect = event.target as HTMLSelectElement;
    this.coordinatorFilter = coordinatorSelect.value;

    // Save coordinator filter to localStorage
    try {
      localStorage.setItem('agentDashboard.coordinatorFilter', this.coordinatorFilter);
    } catch (e) { /* ignore storage errors */ }

    this.applyAllFilters();
  }

  sourceFilterChange(event: Event) {
    this.sourceFilter = (event.target as HTMLSelectElement).value;
    try { localStorage.setItem('agentDashboard.sourceFilter', this.sourceFilter); } catch (e) { /* ignore */ }
    this.applyAllFilters();
  }

  getFilteredRateTotal(): number {
    var sum = this.campaignsFiltered.filter(c => !c.deleted).reduce((sum, c) => sum + (Number(c.rate) || 0), 0);
    return sum;
  }

  getStatusString(campaign: Campaign): string {
    var statusString = '';

    // TODO: set status string based on first incomplete task - or 'Complete' if all completed.

    return statusString;
  }

  getCampaignStatus(campaign: Campaign): 'red' | 'yellow' | 'green' {
    if (!campaign.tasks || campaign.tasks.length === 0) {
      return 'green'; // No tasks means no overdue/due today
    }

    const today = new Date().toISOString().split('T')[0];

    // Check for overdue tasks (highest priority)
    const hasOverdueTasks = campaign.tasks.some(task => {
      const isPastDue = !task.complete && !task.paymentRelated && task.dueDate < today;
      return isPastDue;
    });

    if (hasOverdueTasks) {
      return 'red';
    }

    // Check for tasks due today
    const hasDueTodayTasks = campaign.tasks.some(task => {
      const isDueToday = !task.complete && !task.paymentRelated && task.dueDate === today;
      return isDueToday;
    });

    if (hasDueTodayTasks) {
      return 'yellow';
    }    // No overdue or due today tasks
    return 'green';
  }

  getCampaignStatusTooltip(campaign: Campaign): string {
    if (!campaign.tasks || campaign.tasks.length === 0) {
      return 'No tasks';
    }

    const today = new Date().toISOString().split('T')[0];

    // Count overdue tasks
    const overdueTasks = campaign.tasks.filter(task => {
      return !task.complete && !task.paymentRelated && task.dueDate < today;
    });

    // Count tasks due today
    const dueTodayTasks = campaign.tasks.filter(task => {
      return !task.complete && !task.paymentRelated && task.dueDate === today;
    });

    const overdueCount = overdueTasks.length;
    const dueTodayCount = dueTodayTasks.length;

    if (overdueCount > 0 && dueTodayCount > 0) {
      // Red status with both overdue and due today
      const overdueText = overdueCount === 1 ? '1 Task Overdue' : `${overdueCount} Tasks Overdue`;
      const dueTodayText = dueTodayCount === 1 ? '1 Task Due Today' : `${dueTodayCount} Tasks Due Today`;
      return `${overdueText}\n${dueTodayText}`;
    } else if (overdueCount > 0) {
      // Red status with only overdue
      return overdueCount === 1 ? '1 Task Overdue' : `${overdueCount} Tasks Overdue`;
    } else if (dueTodayCount > 0) {
      // Yellow status with only due today
      return dueTodayCount === 1 ? '1 Task Due Today' : `${dueTodayCount} Tasks Due Today`;
    } else {
      // Green status
      return 'No urgent tasks';
    }
  }

  public getGrossBookings() {
    var sum = this.campaigns.filter(c => !c.deleted).reduce((sum, c) => sum + (Number(c.rate) || 0), 0);
    return sum;
  }

  getActiveCampaignsCurrentMonth(): number {
    const now = new Date();
    return this.nonDeletedCampaigns.filter(c => {
      if (!c.createdDate) return false;
      const d = new Date(c.createdDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  }

  getActiveCampaignsSameMonthLastYear(): number {
    const now = new Date();
    return this.nonDeletedCampaigns.filter(c => {
      if (!c.createdDate) return false;
      const d = new Date(c.createdDate);
      return d.getFullYear() === now.getFullYear() - 1 && d.getMonth() === now.getMonth();
    }).length;
  }

  getActiveCampaignsPctChange(): number {
    const prev = this.getActiveCampaignsSameMonthLastYear();
    if (prev === 0) return 0;
    return ((this.getActiveCampaignsCurrentMonth() - prev) / prev) * 100;
  }

  getActiveCampaignsCountChange(): number {
    return this.getActiveCampaignsCurrentMonth() - this.getActiveCampaignsSameMonthLastYear();
  }

  getGrossBookingsCurrentMonth(): number {
    const now = new Date();
    return this.nonDeletedCampaigns
      .filter(c => {
        if (!c.createdDate) return false;
        const d = new Date(c.createdDate);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((sum, c) => sum + (Number(c.rate) || 0), 0);
  }

  getGrossBookingsSameMonthLastYear(): number {
    const now = new Date();
    return this.nonDeletedCampaigns
      .filter(c => {
        if (!c.createdDate) return false;
        const d = new Date(c.createdDate);
        return d.getFullYear() === now.getFullYear() - 1 && d.getMonth() === now.getMonth();
      })
      .reduce((sum, c) => sum + (Number(c.rate) || 0), 0);
  }

  getGrossBookingsPctChange(): number {
    const prev = this.getGrossBookingsSameMonthLastYear();
    if (prev === 0) return 0;
    return ((this.getGrossBookingsCurrentMonth() - prev) / prev) * 100;
  }

  getGrossBookingsDollarChange(): number {
    return this.getGrossBookingsCurrentMonth() - this.getGrossBookingsSameMonthLastYear();
  }

  getAvgRateCurrentMonth(): number {
    const now = new Date();
    const campaigns = this.nonDeletedCampaigns.filter(c => {
      if (!c.createdDate) return false;
      const d = new Date(c.createdDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    if (campaigns.length === 0) return 0;
    return campaigns.reduce((sum, c) => sum + (Number(c.rate) || 0), 0) / campaigns.length;
  }

  getAvgRateLast3Months(): number {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    const campaigns = this.nonDeletedCampaigns.filter(c => {
      if (!c.createdDate) return false;
      const d = new Date(c.createdDate);
      return d >= start && d < end;
    });
    if (campaigns.length === 0) return 0;
    return campaigns.reduce((sum, c) => sum + (Number(c.rate) || 0), 0) / campaigns.length;
  }

  getAvgRatePctChange(): number {
    const prev = this.getAvgRateLast3Months();
    if (prev === 0) return 0;
    return ((this.getAvgRateCurrentMonth() - prev) / prev) * 100;
  }

  getAvgRateDollarChange(): number {
    return this.getAvgRateCurrentMonth() - this.getAvgRateLast3Months();
  }

  formatKDollars(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1000) {
      return `$${(abs / 1000).toFixed(1)}K`;
    }
    return `$${abs.toFixed(0)}`;
  }

  AddNewCampaign() {
    this.showAddNewCampaignDialog = true;
  }

  clearFilters() {
    this.campaignNameFilter = '';
    this.brandFilter = '';
    this.agentFilter = 'All';
    this.creatorFilter = '';
    this.coordinatorFilter = 'All';
    this.sourceFilter = 'All';
    this.idFilter = '';
    this.showDeleted = false;
    this.showCompleted = true;

    try {
      localStorage.setItem('agentDashboard.campaignNameFilter', '');
      localStorage.setItem('agentDashboard.brandFilter', '');
      localStorage.setItem('agentDashboard.agentFilter', 'All');
      localStorage.setItem('agentDashboard.creatorFilter', '');
      localStorage.setItem('agentDashboard.coordinatorFilter', 'All');
      localStorage.setItem('agentDashboard.sourceFilter', 'All');
      localStorage.setItem('agentDashboard.idFilter', '');
      localStorage.setItem('agentDashboard.showDeleted', 'false');
      localStorage.setItem('agentDashboard.showCompleted', 'true');
    } catch (e) { /* ignore storage errors */ }

    this.applyAllFilters();
  }

  onCampaignCreated(campaign: Campaign) {
    this.campaigns.push(campaign);
    this.clearFilters();
  }
}
